<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide Know Your Customer (KYC) information required for corporate accounts.
          This information is required for compliance with anti-money laundering regulations.
        </p>
      </div>
    </help-drawer>
    <v-form name="PershingKYC" class="form" data-test-id="pershing-kyc-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_KYC.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-kyc-header">
              <span class="headerlight">
                Provide <strong>KYC Information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving KYC information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isPershingKYCComplete && formHidden && !isSyncing"
          :submitted="isPershingKYCComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Country Code Fields -->
            
            <v-row>
              <v-col cols="12" sm="4">
                <label for="countryOngoingBusiness" class="required" data-test-id="country-ongoing-business-label">
                  KYC Country of ongoing business
                </label>
                <v-select
                  v-model="kycInfo.countryOngoingBusiness"
                  :items="countryCodeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose Country Code"
                  @change="$v.kycInfo.countryOngoingBusiness.$touch()"
                  :error-messages="countryOngoingBusinessErrors"
                  data-test-id="country-ongoing-business-select"
                  class="mb-2"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <label for="foreignTaxCountry" data-test-id="foreign-tax-country-label">
                  KYC Foreign Tax Country
                </label>
                <v-select
                  v-model="kycInfo.foreignTaxCountry"
                  :items="countryCodeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose Country Code"
                  @change="$v.kycInfo.foreignTaxCountry.$touch()"
                  data-test-id="foreign-tax-country-select"
                  class="mb-2"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <label for="countryHeadquarters" data-test-id="country-headquarters-label">
                  KYC Country of Headquarters
                </label>
                <v-select
                  v-model="kycInfo.countryHeadquarters"
                  :items="countryCodeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose Country Code"
                  @change="$v.kycInfo.countryHeadquarters.$touch()"
                  data-test-id="country-headquarters-select"
                  class="mb-2"
                />
              </v-col>
            </v-row>
            
            <!-- Text Fields -->
            
            <v-row>
              <v-col cols="12" sm="6">
                <label for="naicsCode" class="required" data-test-id="naics-code-label">
                  NAICS Code
                </label>
                <v-text-field
                  outlined
                  type="text"
                  maxlength="256"
                  name="naicsCode"
                  aria-required="true"
                  v-model.trim="kycInfo.naicsCode"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.kycInfo.naicsCode.$touch()"
                  @blur="$v.kycInfo.naicsCode.$touch()"
                  :error-messages="naicsCodeErrors"
                  hide-details="auto"
                  class="mb-2"
                  data-test-id="naics-code-input"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <label for="industryRisk" class="required" data-test-id="industry-risk-label">
                  KYC Industry of Increased Risk
                </label>
                <v-select
                  v-model="kycInfo.industryRisk"
                  :items="industryRiskOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose Industry Risk"
                  @change="$v.kycInfo.industryRisk.$touch()"
                  :error-messages="industryRiskErrors"
                  data-test-id="industry-risk-select"
                  class="mb-2"
                />
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" sm="6">
                <label for="customerType" class="required" data-test-id="customer-type-label">
                  KYC Customer Type
                </label>
                <v-select
                  v-model="kycInfo.customerType"
                  :items="customerTypeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Choose Customer Type"
                  @change="$v.kycInfo.customerType.$touch()"
                  :error-messages="customerTypeErrors"
                  data-test-id="customer-type-select"
                  class="mb-2"
                />
              </v-col>
              <v-col cols="12" sm="6" v-if="kycInfo.customerType === 'AFV'">
                <label for="afvType" class="required" data-test-id="afv-type-label">
                  AFV Type
                </label>
                <v-select
                  v-model="kycInfo.afvType"
                  :items="afvTypeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.kycInfo.afvType.$touch()"
                  :error-messages="afvTypeErrors"
                  data-test-id="afv-type-select"
                  class="mb-2"
                />
              </v-col>
            </v-row>
            
            <!-- Picklist Fields -->
            
            <v-row>
              <v-col cols="12" sm="6">
                <label for="ownershipType" class="required" data-test-id="ownership-type-label">
                  Ownership Type
                </label>
                <v-select
                  v-model="kycInfo.ownershipType"
                  :items="ownershipTypeOptions"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.kycInfo.ownershipType.$touch()"
                  :error-messages="ownershipTypeErrors"
                  data-test-id="ownership-type-select"
                  class="mb-2"
                />
              </v-col>
            </v-row>
            
            <!-- Y/N Radio Button Fields -->
            
            <v-row>
              <v-col cols="12" sm="6">
                <label class="required mb-2 d-block" data-test-id="has-alternate-name-label">
                  Has Alternate Name
                </label>
                <v-radio-group
                  v-model="kycInfo.hasAlternateName"
                  :error-messages="hasAlternateNameErrors"
                  hide-details="auto"
                  row
                  @change="$v.kycInfo.hasAlternateName.$touch()"
                  data-test-id="has-alternate-name-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="has-alternate-name-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="has-alternate-name-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="required mb-2 d-block" data-test-id="bearer-shares-label">
                  KYC Bearer Shares Allowed
                </label>
                <v-radio-group
                  v-model="kycInfo.bearerShares"
                  :error-messages="bearerSharesErrors"
                  hide-details="auto"
                  row
                  @change="$v.kycInfo.bearerShares.$touch()"
                  data-test-id="bearer-shares-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="bearer-shares-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="bearer-shares-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="pershing-kyc-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, COUNTRY_CODES } from '@/common/constants';
import { required, requiredIf } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PershingKYC',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    kycInfo: {
      countryOngoingBusiness: null,
      foreignTaxCountry: null,
      countryHeadquarters: null,
      naicsCode: null,
      industryRisk: null,
      customerType: null,
      afvType: null,
      ownershipType: null,
      hasAlternateName: null,
      bearerShares: null,
    },
    MENUS: MENUS,
    // Country options will be handled by computed property
    industryRiskOptions: [
      { text: 'None (Not Applicable)', value: 'None (Not Applicable)' },
      { text: 'Charities, Foundations', value: 'Charities, Foundations' },
      { text: 'Digital Assets', value: 'Digital Assets' },
      { text: 'Real Estate', value: 'Real Estate' },
      { text: 'Shipping/ Freight/Transport', value: 'Shipping/ Freight/Transport' },
    ],
    customerTypeOptions: [
      { text: 'Corp/LLC/Partnership', value: 'Corp/LLC/Partnership' },
      { text: 'AFV', value: 'AFV' },
      { text: 'Non-Profit', value: 'Non-Profit' },
    ],
    afvTypeOptions: [
      { text: 'Special Purpose Vehicle', value: 'Special Purpose Vehicle' },
      { text: 'Orphan SPV', value: 'Orphan SPV' },
      { text: 'Private Investment Company "PIC"', value: 'Private Investment Company "PIC"' },
      { text: 'Operating PIC', value: 'Operating PIC' },
      { text: 'Offshore PIC/International Business Corp "IBC"', value: 'Offshore PIC/International Business Corp "IBC"' },
      { text: 'Offshore Operating PIC/IBC', value: 'Offshore Operating PIC/IBC' },
    ],
    ownershipTypeOptions: [
      { text: 'Public', value: 'Public' },
      { text: 'Private', value: 'Private' },
      { text: 'Gov\'t', value: 'Gov\'t' },
    ],
  }),
  
  validations() {
    return {
      kycInfo: {
        countryOngoingBusiness: { required },
        foreignTaxCountry: {},
        countryHeadquarters: {},
        naicsCode: { required },
        industryRisk: { required },
        customerType: { required },
        afvType: { requiredIf: requiredIf(function() { return this.kycInfo.customerType === 'AFV'; }) },
        ownershipType: { required },
        hasAlternateName: { required },
        bearerShares: { required },
      },
      validationGroup: ['kycInfo'],
    };
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isPershingKYCComplete() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_KYC.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_KYC.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'Know Your Customer (KYC) Information';
    },

    // Create Pershing-specific country list with US at top
    countryCodeOptions() {
      if (!Array.isArray(COUNTRY_CODES)) return [];
      const codes = [...COUNTRY_CODES];
      const usIndex = codes.indexOf('US');
      if (usIndex > 0) {
        codes.splice(usIndex, 1);
        return ['US', ...codes];
      }
      return codes;
    },
    
    // Error messages for country fields
    countryOngoingBusinessErrors() {
      const errors = [];
      if (!this.$v.kycInfo.countryOngoingBusiness.$dirty) return errors;
      !this.$v.kycInfo.countryOngoingBusiness.required && errors.push('Country of ongoing business is required');
      return errors;
    },
    
    // Error messages for text fields
    naicsCodeErrors() {
      const errors = [];
      if (!this.$v.kycInfo.naicsCode.$dirty) return errors;
      !this.$v.kycInfo.naicsCode.required && errors.push('NAICS Code is required');
      return errors;
    },
    
    industryRiskErrors() {
      const errors = [];
      if (!this.$v.kycInfo.industryRisk.$dirty) return errors;
      !this.$v.kycInfo.industryRisk.required && errors.push('Industry Risk is required');
      return errors;
    },
    
    customerTypeErrors() {
      const errors = [];
      if (!this.$v.kycInfo.customerType.$dirty) return errors;
      !this.$v.kycInfo.customerType.required && errors.push('Customer Type is required');
      return errors;
    },
    
    
    
    // Error messages for picklist fields
    afvTypeErrors() {
      const errors = [];
      if (!this.$v.kycInfo.afvType.$dirty) return errors;
      !this.$v.kycInfo.afvType.requiredIf && errors.push('AFV Type is required');
      return errors;
    },
    
    ownershipTypeErrors() {
      const errors = [];
      if (!this.$v.kycInfo.ownershipType.$dirty) return errors;
      !this.$v.kycInfo.ownershipType.required && errors.push('Ownership Type is required');
      return errors;
    },
    
    // Error messages for Y/N radio buttons
    hasAlternateNameErrors() {
      const errors = [];
      if (!this.$v.kycInfo.hasAlternateName.$dirty) return errors;
      !this.$v.kycInfo.hasAlternateName.required && errors.push('This field is required');
      return errors;
    },
    
    bearerSharesErrors() {
      const errors = [];
      if (!this.$v.kycInfo.bearerShares.$dirty) return errors;
      !this.$v.kycInfo.bearerShares.required && errors.push('This field is required');
      return errors;
    },
  },
  
  methods: {
    next(route = null) {
      let to = route;
      
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        // If form is hidden, compute next using the latest pages list when route not provided
        const updatedPages = this.interviewPages.getInterviewPages();
        const currentIndex = updatedPages.findIndex((p) =>
          typeof p === 'object' &&
          p.name === MENUS.CLIENT.PERSHING_KYC.id &&
          p.params &&
          p.params.name === this.name &&
          p.params.label === this.label
        );
        const fallbackIndex = currentIndex === -1
          ? updatedPages.findIndex((p) => (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.PERSHING_KYC.id)
          : currentIndex;
        to = to || updatedPages[fallbackIndex + 1];
        this.goNext(to);
        return;
      }
      
      const data = {
        instance: this.instance,
        kycInfo: this.kycInfo,
      };
      
      // Mark page as syncing for immediate UI feedback
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_KYC.id, this.instance);
      
      this.completeStep(MENUS.CLIENT.PERSHING_KYC.id, data, this.instance)
        .then(() => {
          // After progress refresh, recalc pages so any newly available pages are respected
          const updatedPages = this.interviewPages.getInterviewPages();
          const currentIndex = updatedPages.findIndex((p) =>
            typeof p === 'object' &&
            p.name === MENUS.CLIENT.PERSHING_KYC.id &&
            p.params &&
            p.params.name === this.name &&
            p.params.label === this.label
          );
          const fallbackIndex = currentIndex === -1
            ? updatedPages.findIndex((p) => (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.PERSHING_KYC.id)
            : currentIndex;
          to = route || updatedPages[fallbackIndex + 1];
          this.goNext(to);
        })
        .catch(() => {});
    },
    
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },
    
    skip() {
      this.goNext(this.pages[this.pageIndex + 1]);
    },
    
    leave() {
      const route = this.leaveRoute;
      
      const data = {
        instance: this.instance,
        kycInfo: this.kycInfo,
      };
      
      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_KYC.id, this.instance);
      
      this.completeStep(MENUS.CLIENT.PERSHING_KYC.id, data, this.instance)
        .then(() => {
          this.goNext(route);
        })
        .catch(() => {});
    },
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))) {
        this.pageIndex = i;
      }
      i++;
    }
    
    this.formHidden = this.isPershingKYCComplete;
  },
};
</script>

<style lang="scss" scoped>
.syncing-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.step-form-container {
  &.large {
    max-width: 1000px;
  }
}

.help-link {
  padding-top: 8px;
  
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.skip-for-now {
  margin-top: 20px;
  text-align: center;
  
  a {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

/* No section headers on this page */
</style>
